import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, ShieldCheck } from "lucide-react";
import { MaskWords, Eyebrow } from "../site/Reveal";
import { QUOTE_PATH, scrollToId } from "../../lib/site";

const ease = [0.22, 1, 0.36, 1];

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const sunY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="relative [perspective:1400px]">
      <motion.div aria-hidden="true" style={{ y: sunY }} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, delay: 0.5, ease }}
        className="absolute -right-10 -top-12 h-48 w-48 rounded-full bg-gold sm:h-64 sm:w-64 lg:-right-16 lg:-top-16 lg:h-80 lg:w-80" />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div initial={{ clipPath: "inset(100% 0% 0% 0% round 2.25rem)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 2.25rem)" }}
          transition={{ duration: 1.4, delay: 0.25, ease }}
          className="relative aspect-[4/5] w-full overflow-hidden rounded-[2.25rem] bg-forest sm:aspect-[5/5] lg:aspect-[4/5]">
          <motion.img style={{ y: imgY }} initial={{ scale: 1.25 }} animate={{ scale: 1.08 }} transition={{ duration: 2.2, delay: 0.25, ease }}
            src="/images/hero.jpg" alt="Painéis solares fotovoltaicos instalados num telhado de telha cerâmica com luz natural"
            fetchpriority="high" className="absolute inset-0 h-[112%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        </motion.div>
        <motion.div style={{ transform: "translateZ(60px)" }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.3, ease }}
          className="absolute -bottom-6 left-4 right-4 rounded-2xl border border-white/30 bg-white/80 p-4 backdrop-blur-xl sm:left-auto sm:-left-10 sm:right-auto sm:w-72 sm:p-5">
          <p className="eyebrow text-forest/70">Cada projeto</p>
          <p className="mt-2 font-display text-lg font-medium leading-snug tracking-tight text-forest">Dimensionado a partir do seu consumo real.</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const HomeHero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden bg-paper pt-28 pb-24 sm:pt-36 lg:pt-40 lg:pb-32">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(46,111,166,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(46,111,166,0.08)_1px,transparent_1px)] bg-[size:88px_88px] [mask-image:radial-gradient(ellipse_at_top_left,black_30%,transparent_75%)]" />
    <div className="container-x relative grid items-center gap-16 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Eyebrow>Energia solar fotovoltaica · Portugal</Eyebrow>
        </motion.div>
        <MaskWords testId="hero-title" className="mt-7 font-display text-[2.55rem] font-semibold leading-[0.98] tracking-tighter text-forest sm:text-6xl lg:text-[4.1rem] xl:text-[4.75rem]"
          words={"Transforme a energia do sol numa vantagem para si e para".split(" ").map((w) => [w]).concat([["a", 1], ["sua", 1], ["empresa.", 1]])} />
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease }}
          className="mt-8 max-w-xl text-base leading-relaxed text-ink/80 md:text-lg">
          A Mega Solar apresenta soluções de energia solar adaptadas a habitações e empresas, para aproveitar o potencial do sol e melhorar a eficiência energética.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease }} className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Link to={QUOTE_PATH} className="btn-gold group" data-testid="hero-quote-button">Pedir Orçamento Gratuito <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" /></Link>
          <button type="button" onClick={() => scrollToId("solucoes")} className="btn-outline group" data-testid="hero-solutions-button">Conhecer as Nossas Soluções <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" /></button>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.1 }} data-testid="hero-trust-note"
          className="mt-8 flex items-center gap-2.5 text-sm text-ink/75">
          <ShieldCheck className="h-5 w-5 shrink-0 text-leaf-ink" strokeWidth={1.6} />
          Avaliação e proposta sem custos nem compromisso, com base nas suas necessidades.
        </motion.p>
      </div>
      <div className="lg:col-span-5"><HeroVisual /></div>
    </div>
  </section>
);
